import { FloatingNav, OverlaySideNav, HeroSection, NavSection } from '@/components/layout';
import { FAQSearch } from './FAQSearch';
import type { FAQItem, FAQCategory } from '@/data/types';
import {
  HelpCircle,
  Target,
  Users,
  BarChart3,
  FileText,
} from 'lucide-react';

interface CategoryOption {
  id: FAQCategory | 'all';
  label: string;
}

interface FAQPageProps {
  rolePrefix: 'physician' | 'non-physician';
  items: FAQItem[];
  categories: CategoryOption[];
}

const config = {
  physician: {
    badgeText: 'Physician Leadership',
    accentBg: 'bg-eventus-purple/5',
    accentBorder: 'border-eventus-purple/20',
    subtitle: 'For CMO and State CMOs',
    role: 'physician' as const,
  },
  'non-physician': {
    badgeText: 'APC & Teletriage Leadership',
    accentBg: 'bg-eventus-teal/5',
    accentBorder: 'border-eventus-teal/20',
    subtitle: 'For ADCOEs, Director of Teletriage, & RDCOEs',
    role: 'non-physician' as const,
  },
} as const;

// Category icons mapping (matches FAQCategory type from types.ts)
const categoryIcons: Record<FAQCategory | 'all', React.ReactNode> = {
  all: <HelpCircle className="w-4 h-4" />,
  eligibility: <Users className="w-4 h-4" />,
  general: <HelpCircle className="w-4 h-4" />,
  measures: <Target className="w-4 h-4" />,
  thresholds: <BarChart3 className="w-4 h-4" />,
  exceptions: <FileText className="w-4 h-4" />,
  process: <Users className="w-4 h-4" />,
  communication: <FileText className="w-4 h-4" />,
};

export function FAQPage({ rolePrefix, items, categories }: FAQPageProps): JSX.Element {
  const { badgeText, accentBg, accentBorder, subtitle, role } = config[rolePrefix];

  // Convert categories to NavSection format for the overlay nav (exclude 'all' since it's a filter)
  const navSections: NavSection[] = categories
    .filter((cat) => cat.id !== 'all')
    .map((cat) => ({
      id: `category-${cat.id}`,
      label: cat.label,
      icon: categoryIcons[cat.id] || <HelpCircle className="w-4 h-4" />,
    }));

  return (
    <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
      <FloatingNav items={[
        { id: 'framework', label: 'Framework', path: `/${rolePrefix}/framework` },
        { id: 'leader-guide', label: 'Leader Guide', path: `/${rolePrefix}/leader-guide` },
        { id: 'faq', label: 'FAQ', path: `/${rolePrefix}/faq` },
        { id: 'leadership', label: 'Leadership Dashboard', path: '/leadership' },
      ]} />

      {/* Overlay Side Navigation */}
      <OverlaySideNav sections={navSections} />

      {/* Hero Section */}
      <HeroSection
        role={role}
        badgeText={badgeText}
        title="Leader Conversation FAQ"
        description="What your clinicians will ask — and how to respond"
        subtitle={subtitle}
      />

      {/* Content Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`mb-8 p-6 ${accentBg} rounded-xl border ${accentBorder}`}>
            <p className="text-gray-700 leading-relaxed">
              This FAQ helps you prepare for{' '}
              <strong>real-world questions, concerns, and pushback</strong>. The
              intent is not to script conversations, but to provide{' '}
              <strong>clear, calm framing</strong> that reinforces
              Outcomes-Centered Healthcare (OCH) and maintains trust.
            </p>
          </div>

          <FAQSearch items={items} categories={categories} />
        </div>
      </section>
    </div>
  );
}
